import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { mfaSetupGuard } from './guards/mfa-setup.guard';
import { roleGuard } from './guards/role.guard';

/**
 * Ready-to-spread auth routes for consuming apps.
 * Provides /sign-in, /session-expired, /sign-out-all, /auth/callback, and /auth/select-tenant.
 *
 * Usage in app.routes.ts:
 *   import { happenAuthRoutes } from '@happen/auth';
 *   export const routes: Routes = [
 *     ...happenAuthRoutes,
 *     { path: 'dashboard', canActivate: [authGuard], ... },
 *   ];
 */
export const happenAuthRoutes: Routes = [
  {
    path: 'my-access',
    loadComponent: () =>
      import('./components/my-access/my-access.component').then((m) => m.MyAccessComponent),
  },
  {
    path: 'sign-in',
    loadComponent: () =>
      import('./components/sign-in/sign-in.component').then((m) => m.SignInComponent),
  },
  {
    // /activate/error — must come BEFORE /activate/:token so the literal "error" is not captured as a token
    path: 'activate/error',
    loadComponent: () =>
      import('./components/account-activation/account-activation.component').then(
        (m) => m.AccountActivationComponent
      ),
    // No authGuard — link-error state requires no authentication
  },
  {
    path: 'activate/:token',
    loadComponent: () =>
      import('./components/account-activation/account-activation.component').then(
        (m) => m.AccountActivationComponent
      ),
    // No authGuard — pre-authentication screen accessed from the invitation email
  },
  {
    path: 'session-expired',
    loadComponent: () =>
      import('./components/session-expired/session-expired.component').then(
        (m) => m.SessionExpiredComponent
      ),
  },
  {
    path: 'mfa-setup',
    // cc-9 (B1): continuation-aware guard admits the session-less enrolment entry (continuation +
    // setupToken in the URL) while preserving authGuard protection for authenticated entries.
    canActivate: [mfaSetupGuard],
    loadComponent: () =>
      import('./components/mfa-setup/mfa-setup.component').then((m) => m.MfaSetupComponent),
  },
  {
    path: 'recovery-codes',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./components/recovery-codes/recovery-codes.component').then(
        (m) => m.RecoveryCodesComponent
      ),
  },
  {
    path: 'mfa-challenge',
    // No authGuard — user is not yet authenticated when this route is active
    loadComponent: () =>
      import('./components/mfa-challenge/mfa-challenge.component').then((m) => m.MfaChallengeComponent),
  },
  {
    path: 'mfa-recovery',
    // No authGuard — user is not yet authenticated
    loadComponent: () =>
      import('./components/mfa-recovery/mfa-recovery.component').then((m) => m.MfaRecoveryComponent),
  },
  {
    // UC-AUTH-016 — the "sign out of every Happen application on this browser" landing, which each
    // product's signed-out page links to. NO authGuard, deliberately: the user arrives by
    // full-document navigation from another origin, so this tab holds no access token and a guard
    // would ask them to sign IN before they could sign OUT. The credential is the auth-server session
    // cookie, which POST /session/signout-all authenticates on directly.
    path: 'sign-out-all',
    loadComponent: () =>
      import('./components/sign-out-everywhere/sign-out-everywhere.component').then(
        (m) => m.SignOutEverywhereComponent
      ),
  },
  {
    path: 'account-locked',
    loadComponent: () =>
      import('./components/account-locked/account-locked.component').then(
        (m) => m.AccountLockedComponent
      ),
  },
  {
    path: 'account-not-recognised',
    loadComponent: () =>
      import('./components/account-not-recognised/account-not-recognised.component').then(
        (m) => m.AccountNotRecognisedComponent
      ),
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./components/auth-layout/auth-layout.component').then((m) => m.AuthLayoutComponent),
    children: [
      {
        path: 'callback',
        loadComponent: () =>
          import('./components/callback/callback.component').then((m) => m.CallbackComponent),
      },
      {
        path: 'select-tenant',
        loadComponent: () =>
          import('./components/tenant-selector/tenant-selector.component').then(
            (m) => m.TenantSelectorComponent
          ),
      },
    ],
  },
];

/**
 * User management routes for authenticated shell apps.
 * Spread into the shell's children array — NOT into happenAuthRoutes
 * (which are centred auth-layout screens only).
 *
 * Usage in consuming app shell:
 *   { path: 'app', canActivate: [authGuard], children: [...happenUserManagementRoutes] }
 */
export const happenUserManagementRoutes: Routes = [
  {
    path: 'users',
    canActivate: [authGuard, roleGuard],
    data: { requireAdminRole: true },
    loadComponent: () =>
      import('./components/user-management/user-list.component').then(
        (m) => m.UserListComponent
      ),
  },
  {
    path: 'users/:userId',
    canActivate: [authGuard, roleGuard],
    data: { requireAdminRole: true },
    loadComponent: () =>
      import('./components/user-management/user-detail.component').then(
        (m) => m.UserDetailComponent
      ),
  },
  {
    path: 'account/security',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./components/account-security/account-security.component').then(
        (m) => m.AccountSecurityComponent
      ),
  },
  {
    // Story 9.1 (AC-9.1.1 / OQ-1) — customer profile self-service. A SIBLING route to account/security
    // (a self-contained page-component, NOT a tab on AccountSecurityComponent). authGuard ONLY — profile
    // self-service is reachable by ANY signed-in user and is NOT role-gated (unlike the `users` routes).
    path: 'account/profile',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./components/account-profile/account-profile.component').then(
        (m) => m.AccountProfileComponent
      ),
  },
  // NOTE (Story 10.1, AC-10.1.4): the Happen-staff-only admin routes
  // (`admin/tenants`, `admin/applications`) were moved OUT of this constant into
  // `happenInternalAdminRoutes`, exported via the `@happen/auth/admin` secondary
  // entry point and consumed only by the private `happen-internal-admin-app`.
  // The public app spreads `...happenUserManagementRoutes` and therefore no
  // longer reaches the staff-admin screens. The per-tenant routes below
  // (`users`, `users/:userId`, `account/security`) stay in the public app —
  // Epic 9.2 re-gates them to per-(app,tenant) Admission.TenantAdmin.
];
